import React, { useEffect, useState } from "react";
import { TrendingUp, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const STEP_LABELS = [
  { key: "webinar_views", label: "Webinar Views" },
  { key: "apply_starts", label: "Apply Starts" },
  { key: "apply_completes", label: "Apply Completes" },
  { key: "paid", label: "Paid" },
];

const RATE_LABELS = [
  { key: "webinar_to_apply_start", from: "Webinar → Apply" },
  { key: "apply_start_to_complete", from: "Start → Complete" },
  { key: "apply_complete_to_paid", from: "Complete → Paid" },
];

export default function FunnelStrip() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics/funnel?days=${days}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [days]);

  if (loading && !data) return <div className="text-[#A1A1AA] text-sm" data-testid="funnel-loading">Loading funnel…</div>;
  if (!data) return null;

  const maxDaily = Math.max(
    1,
    ...data.daily.map(d => Math.max(d.webinar_views, d.apply_starts, d.apply_completes, d.paid))
  );

  return (
    <div className="border border-[#2B2B2B] bg-[#111111] p-5 mb-8" data-testid="funnel-strip">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="text-[#FF6A00]" size={22}/>
          <h3 className="font-bebas text-2xl uppercase tracking-widest">Conversion Funnel · Last {data.days} Days</h3>
        </div>
        <div className="flex items-center gap-2">
          {[7, 14, 30].map(d => (
            <button key={d} onClick={()=>setDays(d)}
              data-testid={`funnel-range-${d}`}
              className={`text-xs uppercase tracking-widest px-3 py-1 border ${days === d ? "border-[#FF6A00] text-[#FF6A00]" : "border-[#2B2B2B] text-[#A1A1AA]"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Steps row */}
      <div className="grid grid-cols-4 gap-2 md:gap-3 mb-4">
        {STEP_LABELS.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="bg-[#0A0A0A] border border-[#2B2B2B] p-3" data-testid={`funnel-step-${s.key}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA]">{s.label}</div>
              <div className="font-bebas text-3xl text-[#FF6A00] leading-none mt-1">
                {data.steps[s.key] ?? 0}
              </div>
              {i < STEP_LABELS.length - 1 && (
                <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] mt-2 flex items-center gap-1">
                  <ChevronRight size={11}/>
                  <span data-testid={`funnel-rate-${RATE_LABELS[i].key}`}>
                    {data.conversion[RATE_LABELS[i].key]}%
                  </span>
                </div>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* Overall + Diag applications */}
      <div className="flex flex-wrap gap-4 text-xs mb-5 pb-4 border-b border-[#2B2B2B]">
        <div>
          <span className="text-[#A1A1AA] uppercase tracking-widest">Webinar → Paid: </span>
          <span className="text-[#FF6A00] font-bebas text-lg tracking-wider" data-testid="funnel-overall">
            {data.conversion.webinar_to_paid}%
          </span>
        </div>
        <div>
          <span className="text-[#A1A1AA] uppercase tracking-widest">Diagnostic apps in window: </span>
          <span className="text-[#E6E6E6] font-bebas text-lg tracking-wider" data-testid="funnel-diagnostic-apps">
            {data.steps.diagnostic_applications}
          </span>
        </div>
      </div>

      {/* Daily bars */}
      <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] mb-2">Daily activity</div>
      <div className="flex items-end gap-1 h-24" data-testid="funnel-daily-chart">
        {data.daily.map((d, i) => (
          <div key={i} className="flex-1 flex flex-col items-center justify-end gap-0.5" title={`${d.date} — views:${d.webinar_views} starts:${d.apply_starts} apps:${d.apply_completes} paid:${d.paid}`}>
            <div className="w-full flex items-end gap-[2px] h-20">
              <div className="flex-1 bg-[#A1A1AA]/40" style={{ height: `${(d.webinar_views / maxDaily) * 100}%` }} />
              <div className="flex-1 bg-[#FF6A00]/40" style={{ height: `${(d.apply_starts / maxDaily) * 100}%` }} />
              <div className="flex-1 bg-[#FF6A00]/70" style={{ height: `${(d.apply_completes / maxDaily) * 100}%` }} />
              <div className="flex-1 bg-[#FF6A00]" style={{ height: `${(d.paid / maxDaily) * 100}%` }} />
            </div>
            <div className="text-[9px] text-[#A1A1AA]">{d.date.split(" ")[1]}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-4 text-[10px] text-[#A1A1AA] mt-2">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#A1A1AA]/40 inline-block"/>Views</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#FF6A00]/40 inline-block"/>Starts</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#FF6A00]/70 inline-block"/>Completes</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#FF6A00] inline-block"/>Paid</span>
      </div>
    </div>
  );
}
